'use client';

import { useRef, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { AnimationContainer } from '@/components/ui/animation-container';
import { ArrowRight } from 'lucide-react';
import { TranslationKeys } from '../types';
import { copyOr, type SiteCopy } from '@/lib/marketing/site-copy';

// WorldMap uses Three.js (~167KB) — lazy-load it so it never blocks TTI
const WorldMap = dynamic(() => import('@/components/ui/world-map'), { ssr: false });

export default function CtaSection({ t, copy }: { t: TranslationKeys; copy?: SiteCopy }) {
  const title = copyOr(copy, 'home.cta.title', t.trustedWorldwide);
  const titleHighlight = copyOr(copy, 'home.cta.title_highlight', t.worldwide);
  const subtitle = copyOr(copy, 'home.cta.subtitle', t.ctaDesc);
  const button = copyOr(copy, 'home.cta.button', t.startYourTrial);
  const btnRef = useRef<HTMLDivElement>(null);
  const [btnVisible, setBtnVisible] = useState(false);

  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setBtnVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative overflow-hidden">
      {/* Background with Map */}
      <div className="absolute inset-0 bg-purple-600" />

      {/* World Map as Background */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[90%] md:w-[80%] max-w-5xl h-full">
          <WorldMap
            lineColor="#c4b5fd"
            darkMode={true}
            dots={[
              {
                start: { lat: 40.7128, lng: -74.006 }, // New York
                end: { lat: 51.5074, lng: -0.1278 }, // London
              },
              {
                start: { lat: 51.5074, lng: -0.1278 }, // London
                end: { lat: 48.8566, lng: 2.3522 }, // Paris
              },
              {
                start: { lat: 34.0522, lng: -118.2437 }, // Los Angeles
                end: { lat: 19.4326, lng: -99.1332 }, // Mexico City
              },
              {
                start: { lat: 19.4326, lng: -99.1332 }, // Mexico City
                end: { lat: -23.5505, lng: -46.6333 }, // Sao Paulo
              },
              {
                start: { lat: 48.8566, lng: 2.3522 }, // Paris
                end: { lat: 55.7558, lng: 37.6173 }, // Moscow
              },
              {
                start: { lat: 55.7558, lng: 37.6173 }, // Moscow
                end: { lat: 35.6762, lng: 139.6503 }, // Tokyo
              },
            ]}
          />
        </div>
      </div>

      {/* Content overlay */}
      <div className="relative z-10 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4">
          <AnimationContainer delay={0.1}>
            <div className="flex flex-col items-center justify-center text-center">
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-5">
                {title}{" "}
                <span className="text-purple-200">{titleHighlight}</span>
              </h2>
              <p className="text-purple-100 max-w-lg mx-auto text-lg md:text-xl mb-8">
                {subtitle}
              </p>
              <div
                ref={btnRef}
                style={{
                  opacity: btnVisible ? 1 : 0,
                  transform: btnVisible ? 'scale(1)' : 'scale(0.9)',
                  transition: 'opacity 0.5s ease-out 0.2s, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.2s',
                }}
              >
                <a
                  href="/sign-up"
                  onClick={(e) => { e.preventDefault(); window.location.href = '/sign-up'; }}
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-purple-900 rounded-full px-8 h-12 font-semibold shadow-lg no-underline transition-colors touch-manipulation cursor-pointer text-base"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {button}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
          </AnimationContainer>
        </div>
      </div>
    </section>
  );
}
